import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type FaqItem = { q: string; a: string };

const FaqSection = () => {
  const { t } = useTranslation('editor');
  const [open, setOpen] = useState<number | null>(0);
  const raw = t('faq.items', { returnObjects: true });
  const items: FaqItem[] = Array.isArray(raw) ? (raw as FaqItem[]) : [];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 border border-blue-200 bg-blue-50 text-blue-700">
            <HelpCircle className="h-3.5 w-3.5" />
            <span className="text-sm font-semibold tracking-wide">{t('faq.badge')}</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-slate-900 mb-4">{t('faq.title')}</h2>
          <p className="text-lg text-slate-500">{t('faq.subtitle')}</p>
        </motion.div>

        <div className="space-y-3">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className={`rounded-2xl border transition-colors ${isOpen ? 'border-cyan-300 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-semibold text-slate-900">{item.q}</span>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-slate-900 text-white rotate-45' : 'bg-slate-100 text-slate-600'}`}>
                    <Plus className="h-4 w-4" />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="px-6 pb-6 text-slate-600 leading-relaxed">{item.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
